import { z } from 'zod'

export const uuid = z.uuid()

/**
 * A calendar date, as text: 'YYYY-MM-DD'.
 *
 * Deliberately NOT `z.coerce.date()`. A trip that starts on the 10th starts on
 * the 10th in São Paulo and in Paris; the moment you turn that into a Date you
 * have silently attached the server's timezone to it and the date will shift by
 * one for somebody. Calendar dates stay strings end to end and land in a
 * Postgres `date` column. Instants — reservation times, deadlines — are a
 * different type (`isoDateTime`) and are stored as `timestamptz`.
 */
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .refine((v) => {
    const [y, m, d] = v.split('-').map(Number) as [number, number, number]
    const probe = new Date(Date.UTC(y, m - 1, d))
    return (
      probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d
    )
  }, 'not a real calendar date')

/** An instant. Always serialised UTC ISO-8601. */
export const isoDateTime = z.iso.datetime({ offset: true })

/** IANA zone name, validated against the runtime's own tz database. */
export const ianaTimeZone = z.string().refine((v) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: v })
    return true
  } catch {
    return false
  }
}, 'not a known IANA time zone')

export const locales = ['pt-BR', 'en'] as const
export type Locale = (typeof locales)[number]
export const localeSchema = z.enum(locales)

/** ISO-4217, upper case. Money never travels as a float — see `amountMinor`. */
export const currencyCode = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'expected an ISO-4217 code such as BRL')

/**
 * Money, in minor units (centavos, cents). Integer, always. A trip total that
 * is out by a rounding cent is a support conversation, and floats guarantee
 * one eventually.
 */
export const amountMinor = z.int()

export type Uuid = z.infer<typeof uuid>
export type IsoDate = z.infer<typeof isoDate>
export type IsoDateTime = z.infer<typeof isoDateTime>
